import { GridFour, List } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";
import { appPaths } from "../Routes";
import {
    defaultPageIndex,
    defaultPageSize,
    usePaginatedDataSources,
} from "../api/data-sources";
import { DataSourceOrderBy } from "../api/models/DataSource";
import Button from "../components/Button";
import ContentContainer from "../components/ContentContainer";
import DataSourcesFilter from "../components/DataSourcesFilter/DataSourcesFilter";
import DataSourcesTable from "../components/DataSourcesTable";
import DataSourcesTileView from "../components/DataSourcesTileView/DataSourcesTileView";
import MainLayout from "../components/MainLayout";
import Pagination from "../components/Pagination";
import Separator from "../components/Separator";
import Typography from "../components/Typography";
import { useStateParams } from "../hooks/useStateParams";

const DataSourcesPage = () => {
    const [selectedTab, setSelectedTab] = useStateParams<string>(
        "table",
        "tab",
        (s) => s,
        (s) => s
    );

    const navigate = useNavigate();
    const {
        dataSources,
        isLoading: isLoadingDataSources,
        orderBy,
        sortOrder,
        setOrderBy,
        setSortOrder,
        setPageSize,
        setPageNumber,
        ...paginationProps
    } = usePaginatedDataSources();

    const navigateToDataSourceDetail = (id: number) => {
        navigate(appPaths.dataSourceDetail(id.toString()));
    };

    const handleOnOrderBy = (newOrderBy: string) => {
        if (newOrderBy === orderBy) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(newOrderBy as DataSourceOrderBy);
            setSortOrder("asc");
        }
    };

    const handleSelectedTabChange = (value: "table" | "tile") => {
        setSelectedTab(value);

        if (value === "table") {
            // Below resets table pagination values
            // as tile view uses max values
            setPageNumber(defaultPageIndex);
            setPageSize(defaultPageSize);
        }
    };

    return (
        <MainLayout>
            <div className="py-8 px-5 flex flex-col gap-6 md:gap-0 md:flex-row md:items-center md:justify-between">
                <Typography
                    variant="display-small"
                    component="h1"
                    className="text-deep-ocean-20"
                >
                    Data Sources
                </Typography>
                <Link to={appPaths.dataSourceAdd}>
                    <Button className="w-full">Add data source</Button>
                </Link>
            </div>
            <Separator />
            <DataSourcesFilter
                sortOrder={sortOrder}
                orderBy={orderBy}
                setSortOrder={setSortOrder}
                setOrderBy={setOrderBy}
                setPageSize={setPageSize}
                setPageNumber={setPageNumber}
                showSortBy={selectedTab === "tile"}
                {...paginationProps}
                additionalActions={
                    <div className="bg-surface-light-2 rounded-full px-2 py-[0.375rem] flex items-center h-[2.875rem]">
                        <button
                            className={clsx(
                                "flex items-center justify-center w-[3.875rem] h-full rounded-full",
                                selectedTab === "table" && "bg-soft-green-80"
                            )}
                            onClick={() => handleSelectedTabChange("table")}
                        >
                            <label className="sr-only">Table view</label>
                            <List size={20} />
                        </button>
                        <button
                            className={clsx(
                                "flex items-center justify-center w-[3.875rem] h-full rounded-full",
                                selectedTab === "tile" && "bg-soft-green-80"
                            )}
                            onClick={() => handleSelectedTabChange("tile")}
                        >
                            <label className="sr-only">Tile view</label>
                            <GridFour size={24} />
                        </button>
                    </div>
                }
            />
            {isLoadingDataSources ? (
                <ContentContainer className="py-6">
                    <div className="flex-1 flex justify-center items-center">
                        <div className="flex flex-col items-center">
                            <p className="mt-2 opacity-60">Loading...</p>
                        </div>
                    </div>
                </ContentContainer>
            ) : (
                <>
                    {dataSources.length === 0 ? (
                        <ContentContainer className="py-6">
                            <div className="flex-1 flex justify-center items-center">
                                <div className="flex flex-col items-center">
                                    <p className="mt-2 opacity-60">
                                        No existing data sources.
                                    </p>
                                </div>
                            </div>
                        </ContentContainer>
                    ) : (
                        <div className="p-6 mt-2">
                            {selectedTab === "table" && (
                                <>
                                    <DataSourcesTable
                                        dataSources={dataSources}
                                        orderBy={orderBy}
                                        onOrderBy={handleOnOrderBy}
                                        sortOrder={sortOrder}
                                        onRowClick={navigateToDataSourceDetail}
                                    />
                                    <Pagination
                                        setPageSize={setPageSize}
                                        setPageNumber={setPageNumber}
                                        {...paginationProps}
                                    />
                                </>
                            )}
                            {selectedTab === "tile" && (
                                <DataSourcesTileView
                                    dataSources={dataSources}
                                    orderBy={orderBy}
                                    onOrderBy={handleOnOrderBy}
                                    sortOrder={sortOrder}
                                    onRowClick={navigateToDataSourceDetail}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </MainLayout>
    );
};

export default DataSourcesPage;
